import React, { useEffect, useState } from 'react';
import Modal from '../common/Modal.js';
import Button from '../common/Button.js';
import FormInput from '../common/FormInput.js';
import SelectField from '../common/SelectField.js';

const ProductDialog = ({
  isOpen,
  onClose,
  onSave,
  product = null,
  inventoryItems = [],
  categoryOptions = [],
  tokenPapersCount = 1,
  loading = false
}) => {
    const [formValues, setFormValues] = useState({
    name: '',
    category: '',
    price: '',
    token_paper: 1
  });
  const [recipeRows, setRecipeRows] = useState([{ inventoryId: '', quantity: '' }]);
  const [errors, setErrors] = useState({});

  // Generate token paper options based on tokenPapersCount
  const tokenPaperOptions = Array.from({ length: tokenPapersCount }, (_, i) => ({
    label: `Paper ${i + 1}`,
    value: (i + 1).toString()
  }));

  useEffect(() => {
    if (product) {
      setFormValues({
        name: product.name || '',
        category: product.category || '',
        price: product.price?.toString() || '',
        token_paper: product.token_paper || 1
      });

      const recipeSource = product.recipe || product.recipeDetails || [];
      setRecipeRows(recipeSource.map((item) => {
        const inventoryId = item.inventoryId || item.inventory_id || item.inventory?.id || '';
        const quantity = item.quantity?.toString() || item.qty?.toString() || '';
        return {
          inventoryId,
          quantity
        };
      }));
    } else {
      setFormValues({ name: '', category: '', price: '', token_paper: 1 });
      setRecipeRows([{ inventoryId: '', quantity: '' }]);
    }
    setErrors({});
  }, [product, isOpen]);

  const handleFieldChange = (field, value) => {
    setFormValues((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: '' }));
    }
  };

  const handleRecipeChange = (index, field, value) => {
    setRecipeRows((prev) => prev.map((row, idx) => (idx === index ? { ...row, [field]: value } : row)));
    if (errors[`recipe-${index}-${field}`]) {
      setErrors((prev) => ({ ...prev, [`recipe-${index}-${field}`]: '' }));
    }
  };

  const addRecipeRow = () => setRecipeRows((prev) => [...prev, { inventoryId: '', quantity: '' }]);
  const removeRecipeRow = (index) => setRecipeRows((prev) => prev.filter((_, idx) => idx !== index));

  const validateForm = () => {
    const nextErrors = {};
    if (!formValues.name.trim()) nextErrors.name = 'Product name is required';
    if (!formValues.category) nextErrors.category = 'Category is required';
    if (!formValues.price || Number(formValues.price) <= 0) nextErrors.price = 'Enter a valid price';

    // Recipe is now optional - only validate rows that have started being filled
    recipeRows.forEach((row, index) => {
      // If inventory is selected, quantity must be valid
      if (row.inventoryId && (!row.quantity || Number(row.quantity) <= 0)) {
        nextErrors[`recipe-${index}-quantity`] = 'Enter a valid amount';
      }
      // If quantity is entered, inventory must be selected
      if (row.quantity && Number(row.quantity) > 0 && !row.inventoryId) {
        nextErrors[`recipe-${index}-inventoryId`] = 'Select an ingredient';
      }
    });

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    const payload = {
      name: formValues.name.trim(),
      category: formValues.category,
      price: Number(formValues.price),
      token_paper: Number(formValues.token_paper),
      recipe: recipeRows
        .filter((row) => row.inventoryId && Number(row.quantity) > 0)
        .map((row) => ({ inventoryId: row.inventoryId, quantity: Number(row.quantity) }))
    };

    onSave(payload);
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={product ? 'Edit Product' : 'Create New Product'} size="large">
      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="grid gap-4 md:grid-cols-2">
          <FormInput
            id="productName"
            name="name"
            label="Product Name"
            placeholder="e.g. Single Scoop Cone"
            value={formValues.name}
            onChange={(e) => handleFieldChange('name', e.target.value)}
            error={errors.name}
            required
          />
          <SelectField
            id="productCategory"
            name="category"
            label="Category"
            options={categoryOptions}
            value={formValues.category}
            onChange={(e) => handleFieldChange('category', e.target.value)}
            error={errors.category}
            required
          />
        </div>

<div className="grid gap-4 md:grid-cols-2">
      <FormInput
        id="productPrice"
        name="price"
        label="Price"
        type="number"
        placeholder="Sale price"
        value={formValues.price}
        onChange={(e) => handleFieldChange('price', e.target.value)}
        error={errors.price}
        required
      />
      <SelectField
        id="tokenPaper"
        name="token_paper"
        label="Token Paper"
        options={tokenPaperOptions}
        value={formValues.token_paper.toString()}
        onChange={(e) => handleFieldChange('token_paper', Number(e.target.value))}
        required
      />
        </div>

        <div className="rounded-3xl bg-gray-50 p-4">
          <div className="flex items-center justify-between gap-4 mb-4">
            <div>
              <h3 className="text-lg font-semibold text-slate-900">Recipe Ingredients</h3>
              <p className="text-sm text-gray-500">Add the inventory items used for this product. (Optional)</p>
            </div>
            <Button variant="secondary" onClick={addRecipeRow}>Add ingredient</Button>
          </div>

          {recipeRows.map((row, index) => (
            <div key={index} className="grid gap-4 md:grid-cols-[1.6fr_1fr_0.4fr] items-end mb-4">
              <div>
                <SelectField
                  id={`recipe-${index}-inventory`}
                  name="inventoryId"
                  label="Ingredient"
                  options={inventoryItems.map((item) => ({ label: item.name, value: item.id }))}
                  value={row.inventoryId}
                  onChange={(e) => handleRecipeChange(index, 'inventoryId', e.target.value)}
                  error={errors[`recipe-${index}-inventoryId`]}
                />
              </div>
              <div>
                <FormInput
                  id={`recipe-${index}-quantity`}
                  name="quantity"
                  label="Quantity"
                  type="number"
                  placeholder="Amount"
                  value={row.quantity}
                  onChange={(e) => handleRecipeChange(index, 'quantity', e.target.value)}
                  error={errors[`recipe-${index}-quantity`]}
                />
              </div>
              <div className="flex items-center justify-end">
                <Button variant="danger" size="small" type="button" onClick={() => removeRecipeRow(index)}>
                  Remove
                </Button>
              </div>
            </div>
          ))}
        </div>

        <Modal.Actions>
          <Button type="button" variant="secondary" onClick={onClose} disabled={loading}>
            Cancel
          </Button>
          <Button type="submit" variant="primary" loading={loading}>
            {product ? 'Save Product' : 'Create Product'}
          </Button>
        </Modal.Actions>
      </form>
    </Modal>
  );
};

export default ProductDialog;
